import React, { useEffect, useState } from 'react';
import {
  Box, Button, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Spinner, Text,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import { getStoredFileObjectUrl, downloadStoredFile } from '../hooks/useStoredFiles';

export interface PreviewFile { id: string; name: string; contentType?: string }

// item 935 (Chad/Jake) — attachments open in an IN-APP preview modal instead of forcing a
// download: images inline, PDFs in an embedded viewer, everything else gets a clean
// "no inline preview" card. Download always available in the footer. Used by task
// attachments and every document tree.
const StoredFilePreviewModal: React.FC<{
  file: PreviewFile | null;
  onClose: () => void;
}> = ({ file, onClose }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setUrl(null);
    setFailed(false);
    if (!file) return undefined;
    let revoked: string | null = null;
    getStoredFileObjectUrl(file.id)
      .then((u) => { revoked = u; setUrl(u); })
      .catch(() => setFailed(true));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [file]);

  const ct = (file?.contentType ?? '').toLowerCase();
  const isImage = ct.startsWith('image/');
  const isPdf = ct === 'application/pdf' || (file?.name ?? '').toLowerCase().endsWith('.pdf');

  return (
    <Modal isOpen={!!file} onClose={onClose} size="5xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pr={12} fontSize="md" noOfLines={1}>{file?.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!url && !failed && (
            <Box textAlign="center" py={16}><Spinner size="lg" /></Box>
          )}
          {failed && (
            <Text color="red.400" py={8} textAlign="center">Could not load the file — try Download instead.</Text>
          )}
          {url && isImage && (
            <Image src={url} alt={file?.name} maxH="70vh" mx="auto" objectFit="contain" />
          )}
          {url && !isImage && isPdf && (
            <Box as="iframe" src={url} title={file?.name} w="100%" h="72vh" borderWidth="1px" borderRadius="md" />
          )}
          {url && !isImage && !isPdf && (
            <Box textAlign="center" py={12}>
              <Text mb={2}>No inline preview for this file type.</Text>
              <Text fontSize="sm" opacity={0.7}>Use Download below to open it in its own app.</Text>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            leftIcon={<FiDownload />}
            variant="outline"
            mr={2}
            onClick={() => file && downloadStoredFile(file.id, file.name)}
          >
            Download
          </Button>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StoredFilePreviewModal;
